// Migration utility to move photos from cache/localStorage to IndexedDB
// This handles photos that were saved before the IndexedDB photo persistence fix

import storageManager from './storageManager.js';

const PhotoMigration = {
  /**
   * Migrate photos from cached tripData to IndexedDB photos object store
   * This should be run once to fix photos that were saved before the persistence fix
   */
  async migrateExistingPhotos() {
    console.log('📸 [PhotoMigration] Starting photo migration...');

    try {
      // Initialize IndexedDB
      await storageManager.initialize();

      // Get the active trip ID
      const activeId = storageManager.getActiveTripId();
      if (!activeId) {
        console.log('📸 [PhotoMigration] No active trip found');
        return { success: false, reason: 'no_active_trip' };
      }

      console.log('📸 [PhotoMigration] Active trip ID:', activeId);

      // Get cached trip data (photos are here)
      const cachedData = window.__cachedTripData;
      if (!cachedData || !cachedData.photos || cachedData.photos.length === 0) {
        console.log('📸 [PhotoMigration] No photos in cache to migrate');
        return { success: true, migrated: 0 };
      }

      console.log('📸 [PhotoMigration] Found photos in cache:', cachedData.photos.length);

      // Get photos already in IndexedDB
      const existingPhotos = await storageManager.getPhotosByTrip(activeId);
      const existingPhotoIds = new Set(existingPhotos.map(p => p.id));
      console.log('📸 [PhotoMigration] Photos already in IndexedDB:', existingPhotos.length);

      // Migrate photos that aren't already in IndexedDB
      let migratedCount = 0;
      for (const photo of cachedData.photos) {
        // Skip if already in IndexedDB
        if (existingPhotoIds.has(photo.id)) {
          console.log('📸 [PhotoMigration] Skipping photo (already in IndexedDB):', photo.id);
          continue;
        }

        try {
          // Create photo entry with tripId
          const photoEntry = {
            id: photo.id,
            tripId: activeId,
            data: photo.data,
            timestamp: photo.timestamp || new Date().toISOString(),
            memoryTitle: photo.memoryTitle || '',
            memoryId: photo.memoryId || null
          };

          // Save to IndexedDB
          await storageManager.savePhoto(photoEntry);
          console.log('📸 [PhotoMigration] ✓ Migrated photo:', photo.id);
          migratedCount++;
        } catch (error) {
          console.error('📸 [PhotoMigration] ✗ Failed to migrate photo:', photo.id, error);
        }
      }

      console.log(`📸 [PhotoMigration] ✅ Migration complete! Migrated ${migratedCount} photos`);

      return {
        success: true,
        migrated: migratedCount,
        total: cachedData.photos.length,
        alreadyInDB: existingPhotos.length
      };

    } catch (error) {
      console.error('📸 [PhotoMigration] ❌ Migration failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

export default PhotoMigration;
